import React, { useRef, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { saveEmailLayout } from './action';
import { CheckCircle } from 'lucide-react';
// Reusable blocks
export type CallToAction = {
  text: string;
  url: string;
  order: number;
};

export type Section = {
  title: string;
  body_text: string;
  order: number;
  height?: number;
};

export type Banner = {
  background_color: string;
  text_data: string;
  order: number;
};

export type TextBlock = {
  text: string;
  order: number;
};

// Main data interface
export type EventData = {
  heading: TextBlock;
  subheading: TextBlock;
  banner: Banner[];
  section: Section[];
  callout_text: TextBlock[];
  call_to_action: CallToAction[];
};

// Images
export type WhitepaperImage = {
  url: string;
  description: string;
  order: number;
};

// Full API response
export interface WhitepaperApiResponse {
  success: boolean;
  user_id: string;
  thread_id: string;
  template: "thought_leadership" | string;
  data: EventData;
  images: WhitepaperImage[];
}

interface ThoughtLeadershipProps {
  jsonData: any;
}

// Define section types for dynamic rendering
type DynamicSection = {
  order: number;
  type: 'heading' | 'subheading' | 'banner' | 'section' | 'callout_text' | 'call_to_action';
  data: any;
  index?: number; // For array items
};

const ThoughtLeadership = ({ jsonData }: ThoughtLeadershipProps) => {
  const { heading, subheading, banner, callout_text, call_to_action, section } =
    jsonData.data; // jsonData is already EventData

  const emailLayoutRef = useRef<HTMLDivElement | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Helper function to safely extract text from potentially nested objects
  const getText = (value: any): string => {
    if (!value) return "";

    // Case 1: plain string
    if (typeof value === "string") return value;

    // Case 2: object with text
    if (typeof value === "object" && !Array.isArray(value) && "text" in value) {
      return String(value.text ?? "");
    }

    // Case 3: array of objects with text
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (typeof item === "string") return item;
          if (item && typeof item === "object" && "text" in item)
            return item.text;
          return "";
        })
        .filter(Boolean)
        .join(" ");
    }

    return "";
  };

  // Create a sorted list of all sections based on their order
  const sortedSections = useMemo(() => {
    const sections: DynamicSection[] = [];

    // Add heading
    if (heading && typeof heading.order === 'number') {
      sections.push({ order: heading.order, type: 'heading', data: heading });
    }

    // Add subheading
    if (subheading && typeof subheading.order === 'number') {
      sections.push({ order: subheading.order, type: 'subheading', data: subheading });
    }

    // Add banners
    if (Array.isArray(banner)) {
      banner.forEach((item, index) => {
        if (typeof item.order === 'number') {
          sections.push({ order: item.order, type: 'banner', data: item, index });
        }
      });
    }

    // Add sections
    if (Array.isArray(section)) {
      section.forEach((item, index) => {
        if (typeof item.order === 'number') {
          sections.push({ order: item.order, type: 'section', data: item, index });
        }
      });
    }

    // Add callout texts
    if (Array.isArray(callout_text)) {
      callout_text.forEach((item, index) => {
        if (typeof item.order === 'number') {
          sections.push({ order: item.order, type: 'callout_text', data: item, index });
        }
      });
    }

    // Add call to actions
    if (Array.isArray(call_to_action)) {
      call_to_action.forEach((item, index) => {
        if (typeof item.order === 'number') {
          sections.push({ order: item.order, type: 'call_to_action', data: item, index });
        }
      });
    }

    // Sort by order
    return sections.sort((a, b) => a.order - b.order);
  }, [heading, subheading, banner, section, callout_text, call_to_action]);

  // Render individual section based on type
  const renderSection = (section: DynamicSection) => {
    const key = `${section.type}-${section.order}-${section.index || 0}`;

    switch (section.type) {
      case 'heading':
        return (
          <div key={key} style={{ padding: '0 20px' }}>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px', fontFamily: 'Arial, Helvetica, sans-serif' }}>
              {getText(section.data)}
            </h1>
          </div>
        );

      case 'subheading':
        return (
          <div key={key} style={{ padding: '0 20px', marginTop: '16px' }}>
            <h2 style={{ fontSize: '18px', color: '#374151', fontWeight: '600', fontFamily: 'Arial, Helvetica, sans-serif' }}>
              {getText(section.data)}
            </h2>
          </div>
        );

      case 'banner':
        return (
          <div
            key={key}
            style={{
              color: "white",
              marginTop: "16px",
              padding: "20px 20px",
              display: "flex",
              backgroundColor: "#004080",
            }}
          >
            {jsonData.images && jsonData.images[0] && (
              <img
                src={jsonData.images[0].url}
                style={{ height: "200px", width: "200px", objectFit: "cover", marginRight: "16px" }}
                alt="Banner"
              />
            )}
            <p style={{ margin: 0, fontFamily: "Arial, Helvetica, sans-serif" }}>
              {getText(section.data.text_data)}
            </p>
          </div>
        );

      case 'section':
        return (
          <div key={key} style={{ padding: '0 20px', marginTop: '16px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#2563eb', fontFamily: 'Arial, Helvetica, sans-serif' }}>
              {getText(section.data.title)}
            </h3>
            <p style={{ color: '#374151', whiteSpace: 'pre-line', margin: 0, fontFamily: 'Arial, Helvetica, sans-serif' }}>
              {getText(section.data.body_text)}
            </p>
          </div>
        );

      case 'callout_text':
        return (
          <div key={key}>
            <div style={{ backgroundColor: '#e5e7eb', height: '2px', margin: '16px 20px', fontFamily: 'Arial, Helvetica, sans-serif' }}></div>
            <div style={{ padding: '0 20px', marginTop: '16px' }}>
              <p style={{  color: '#374151', whiteSpace: 'pre-line', fontFamily: 'Arial, Helvetica, sans-serif' }}>
                {getText(section.data.text)}
              </p>
            </div>
          </div>
        );

      case 'call_to_action':
        return (
          <div key={key} style={{ display: "flex", padding: '0 20px', marginTop: '16px' }}>
            <a
              href={section.data.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                fontSize: '14px',
                padding: '8px 12px',
                backgroundColor: '#2563eb',
                color: 'white',
                fontWeight: '600',
                borderRadius: '0',
                textDecoration: 'none',
                fontFamily: 'Arial, Helvetica, sans-serif',
                marginRight: '12px',
              }}
            >
              {getText(section.data.text)}
            </a>
          </div>
        );

      default:
        return null;
    }
  };

  const handleConfirmLayout = async () => {
    setIsSubmitting(true);
    if (!emailLayoutRef.current) return;

    const rawHtml = emailLayoutRef.current.innerHTML;

    const payload = {
      raw_html: rawHtml,
    };

    try {
      const ans = await saveEmailLayout(jsonData.user_id, jsonData.thread_id, payload);
      
      if (ans?.success === true) {
        setIsSuccess(true);
      } else {
        setError("Failed to save data");
      }
    } catch (error) {
      console.error("Failed to save email layout", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: '36rem' }}>
      {isSuccess ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          border: '1px solid #d1d5db',
          borderRadius: '8px'
        }}>
          <CheckCircle style={{ width: '16px', height: '16px', color: '#16a34a', marginBottom: '16px' }} />
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
            Email Layout saved successfully
          </h2>
          <p style={{ fontSize: '14px', color: '#4b5563', marginTop: '8px', textAlign: 'center' }}>
            Thank you! Your information has been saved successfully.
          </p>
        </div>
      ) : (
        <>
          <div ref={emailLayoutRef} style={{
            height: '86vh',
            border: '1px solid #d1d5db',
            borderRadius: '12px',
            overflowY: 'auto',
          }}>

            <body style={{background:'#eff6ff', padding:'30px', margin:'0', fontFamily: 'Arial, Helvetica, sans-serif'}}>
              <div style={{maxWidth: '700px', marginLeft: 'auto', marginRight: 'auto',fontFamily: 'Arial, Helvetica, sans-serif'}}> 

                {/* Header */}
                <div style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: '#FFFFFF',
                  fontFamily: 'inherit',
                  padding: '15px',
              marginTop:'0',
              boxSizing:'border-box'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <a href="https://www.broadridge.com/" target="_blank" rel="noopener noreferrer">
                      <img src="https://www.broadridge.com/_Static-Assets/images/brodridgeLogo.svg" alt="logo" style={{ width: '112px', height: 'auto', objectFit: 'contain' }} />
                    </a>
                  </div>
                  <div style={{ fontSize: '14px', color: '#4b5563' }}>&nbsp;</div>
                </div>

                {/* Dynamic Content - Rendered in Order */}
                <div style={{ backgroundColor: 'white', paddingTop: '8px', paddingBottom: '8px', fontFamily: 'Arial, Helvetica, sans-serif' }}>
                  {sortedSections.map((section) => renderSection(section))}
                </div>

                {/* Footer */}
                <div style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
                  <div style={{ backgroundColor: '#f0f0f0', padding: '30px 20px' }}>
                    <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'left' }}>
                      <p style={{ fontSize: '14px', lineHeight: '20px', marginBottom: '20px', color: '#3c3c3c' }}>
                        Broadridge Financial Solutions (NYSE: BR) is a global technology leader with
                        trusted expertise and transformative technology, helping clients and the financial
                        services industry operate, innovate, and grow. We power investing, governance,
                        and communications for our clients - driving operational resiliency, elevating
                        business performance, and transforming investor experiences. For more information,
                        please visit <a href="https://broadridge.com" style={{ color: '#2662fc' }}>Broadridge.com</a>.
                      </p>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 0 0' }}>
                        <a href="https://www.broadridge.com/" target="_blank" rel="noopener noreferrer">
                          <img
                            src="https://www.broadridge.com/_Static-Assets/images/brodridgeLogo.svg"
                            alt="Broadridge"
                            style={{ height: '30px', objectFit: 'contain' }}
                          />
                        </a>
                        <div style={{ fontSize: '22px', fontWeight: '400', color: '#000c36' }}>
                          Ready for Next
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Reason for receiving email */}
                  <div style={{ backgroundColor: '#fff', padding: '20px 20px' }}>
                    <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'left' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
                        <div style={{ fontSize: '12px', color: '#999999', lineHeight: '16px' }}>
                          You are receiving this email because <strong>anusha.kakaraparthi@broadridge.com</strong> is signed up
                          to receive Broadridge communications.
                        </div>

                        <div style={{ fontSize: '12px', color: '#999999', lineHeight: '16px' }}>
                          Delivered by Broadridge Financial Solutions, Inc., 5 Dakota Drive, Suite 300,
                          Lake Success, NY 11042, USA
                        </div>
                      </div>

                  {/* Links */}
                  {/* <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                    <a href="#" style={{ color: '#000c36', fontSize: '12px', marginRight: '5px' }}>Unsubscribe</a>
                    /
                    <a href="#" style={{ color: '#000c36', fontSize: '12px', marginLeft: '5px', marginRight: '10px' }}>Manage Preferences</a>
                    /
                    <a href="#" style={{ color: '#000c36', fontSize: '12px', marginLeft: '5px' }}>View as a Webpage</a>
                  </div> */}

                      {/* Disclaimer */}
                  <p style={{ fontSize: '12px', color: '#999999', lineHeight: '16px', marginBottom: '20px' , marginTop: '20px' }}>
                        This message and any attachments are intended only for the use of the addressee and
                        may contain information that is privileged and confidential. If the reader of the message
                        is not the intended recipient or an authorized representative of the intended recipient,
                        you are hereby notified that any dissemination of this communication is strictly prohibited.
                        If you have received this communication in error, please notify us immediately by e-mail
                        and delete the message and any attachments from your system.
                      </p>

                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
                        <div style={{ fontSize: '12px', color: '#777', marginBottom: '20px' }}>
                          © Copyright 2025, Broadridge Financial Solutions, Inc. <br />
                          <a target="_blank" rel="noopener noreferrer" href="https://www.broadridge.com/legal/privacy-statement-english" style={{ color: '#1a0dab' }}>Privacy Statement</a>
                        </div>

                        {/* Social Icons */}
                        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', alignItems: 'center' }}>
                          <a target="_blank" rel="noopener noreferrer" href="https://www.linkedin.com/company/broadridge-financial-solutions/">
                            <img src="https://i.ibb.co/35CWCWMX/br-3.jpg" style={{ width: '24px', height: '24px' }} alt="LinkedIn" />
                          </a>
                          <a target="_blank" rel="noopener noreferrer" href="https://instagram.com/broadridge">
                            <img src="https://i.ibb.co/0R8gGrZv/br-4.jpg" style={{ width: '24px', height: '24px' }} alt="Instagram" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </body>

          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', marginTop: '8px' }}>
            <button
              onClick={handleConfirmLayout}
              disabled={isSubmitting}
              style={{
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.5 : 1,
              }}
              className='rounded-xl  text-sm text-white inline-flex justify-center items-center px-[12px] py-[10px] bg-primary-900 font-medium'
            >
              {isSubmitting ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg style={{ animation: 'spin 1s linear infinite', height: '8px', width: '8px', marginRight: '8px' }} viewBox="0 0 24 24">
                    <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Confirming Email Layout
                </span>
              ) : (
                'Confirm Email Layout'
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default ThoughtLeadership;